import { useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";

/**
 * Renders a long identifier -- an Azure resource path is ~130 characters -- on
 * a single truncated line, with the full value one hover or one click away.
 *
 * This used to be a bare <span> carrying text-overflow: ellipsis, which does
 * nothing on an inline element: the ID wrapped across the detail column instead
 * of truncating. It now lays out as flex so the truncation actually takes
 * effect, renders monospace (these are machine values, read character by
 * character), keeps the full string in a title for hover, and offers copy, so
 * truncating no longer puts the value out of reach.
 */
const NiceId = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef(null);

  // The confirmation resets itself; clear it on unmount so the timer cannot
  // fire into an unmounted tree.
  useEffect(() => () => clearTimeout(resetTimer.current), []);

  if (children === null || children === undefined || children === "") {
    return null;
  }

  const text = String(children);

  const copy = () => {
    const confirm = () => {
      setCopied(true);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    };

    try {
      const written = navigator?.clipboard?.writeText(text);
      if (written && typeof written.then === "function") {
        // Confirm only on success. An insecure context or a denied permission
        // rejects here; the ID is still on screen and selectable by hand, so
        // failing quietly beats ticking a copy that did not happen.
        written.then(confirm, () => {});
      } else if (written !== undefined) {
        confirm();
      }
    } catch {
      /* no clipboard API at all -- nothing to do but leave the ID visible */
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.15rem",
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box
        component="span"
        title={text}
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        {text}
      </Box>
      <IconButton
        size="small"
        aria-label={copied ? "Copied" : "Copy ID"}
        onClick={copy}
        sx={{ flex: "0 0 auto", padding: "2px" }}
      >
        {copied ? (
          <CheckOutlined sx={{ fontSize: "0.9rem" }} />
        ) : (
          <ContentCopyOutlined sx={{ fontSize: "0.9rem" }} />
        )}
      </IconButton>
    </Box>
  );
};

export default NiceId;
